import React, { useState } from 'react';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Check,
  CheckCircle2,
  Copy,
  ExternalLink,
  FileSpreadsheet,
  Shield,
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import type { FixedColumnDef, OptionalColumnDef } from './types';

export interface GenericGoogleSheetsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  entityName: string;
  totalCount?: number;
  fixedColumns: FixedColumnDef[];
  optionalColumns: OptionalColumnDef[];
  onExport: (selectedColumnIds: string[]) => Promise<string>;
}

export const GenericGoogleSheetsModal: React.FC<GenericGoogleSheetsModalProps> = ({
  open,
  onOpenChange,
  title,
  description,
  entityName,
  totalCount = 0,
  fixedColumns,
  optionalColumns,
  onExport,
}) => {
  const [selectedColumns, setSelectedColumns] = useState<string[]>(
    optionalColumns.filter((c) => c.defaultSelected !== false).map((c) => c.id)
  );
  const [exportState, setExportState] = useState<'idle' | 'connecting' | 'exporting' | 'done'>('idle');
  const [sheetUrl, setSheetUrl] = useState<string | null>(null);
  const [copyLabel, setCopyLabel] = useState('Copy Link');

  const toggleColumn = (id: string) => {
    setSelectedColumns((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  };

  const isAllSelected = selectedColumns.length === optionalColumns.length;

  const handleToggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedColumns([]);
    } else {
      setSelectedColumns(optionalColumns.map((c) => c.id));
    }
  };

  const handleExport = async () => {
    try {
      setExportState('connecting');
      const url = await onExport(selectedColumns);
      setSheetUrl(url);
      setExportState('done');
      toast({
        title: 'Export Successful',
        description: `Your ${entityName.toLowerCase()} have been exported to Google Sheets.`,
      });
    } catch (err: any) {
      setExportState('idle');
      toast({
        title: 'Export Failed',
        description: err.message || 'An error occurred during Google Sheets export.',
        variant: 'destructive',
      });
    }
  };

  const handleCopyLink = async () => {
    if (!sheetUrl) return;
    await navigator.clipboard.writeText(sheetUrl);
    setCopyLabel('Copied!');
    setTimeout(() => setCopyLabel('Copy Link'), 2000);
  };

  const resetState = () => {
    setExportState('idle');
    setSheetUrl(null);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(val) => {
        onOpenChange(val);
        if (!val) setTimeout(resetState, 300);
      }}
    >
      <DialogContent className="sm:max-w-[580px] p-6 sm:p-7 rounded-[24px] border border-slate-100 bg-white shadow-2xl font-['Hanken_Grotesk',sans-serif] overflow-hidden">
        {exportState === 'done' && sheetUrl ? (
          <div className="w-full min-w-0">
            <DialogHeader className="mb-2">
              <div className="flex items-center gap-3.5">
                <div className="w-12 h-12 bg-emerald-50 rounded-[14px] flex items-center justify-center shrink-0 border border-emerald-100">
                  <FileSpreadsheet className="w-6 h-6 text-emerald-600 stroke-[1.8]" />
                </div>
                <div>
                  <DialogTitle className="text-[20px] font-bold text-slate-900 leading-tight">
                    Your Google Sheet is ready!
                  </DialogTitle>
                  <p className="text-[13px] text-slate-500 mt-0.5">
                    Anyone with the link can view it
                  </p>
                </div>
              </div>
            </DialogHeader>

            <div className="space-y-4 mt-5 w-full min-w-0">
              <div className="bg-slate-50 border border-slate-200 rounded-[12px] px-3.5 py-2.5 text-[13px] text-slate-700 truncate w-full font-mono">
                {sheetUrl}
              </div>

              <div className="flex gap-3 w-full">
                <Button
                  type="button"
                  onClick={handleCopyLink}
                  variant="outline"
                  className="flex-1 gap-2 border-slate-200 hover:bg-slate-50 rounded-[12px] h-11 text-[14px]"
                >
                  {copyLabel === 'Copied!' ? (
                    <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                  ) : (
                    <Copy className="w-4 h-4 shrink-0 text-slate-500" />
                  )}
                  <span className="truncate">{copyLabel}</span>
                </Button>
                <Button
                  asChild
                  className="flex-1 gap-2 bg-[#A655F8] hover:bg-[#9333EA] text-white rounded-[12px] h-11 text-[14px]"
                >
                  <a
                    href={sheetUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center w-full"
                  >
                    <ExternalLink className="w-4 h-4 shrink-0 mr-2" />
                    <span className="truncate">Open in Sheets</span>
                  </a>
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="w-full min-w-0 space-y-5">
            <div className="flex items-center gap-3.5 text-left">
              <div className="w-12 h-12 rounded-[14px] bg-[#FAF5FF] flex items-center justify-center text-[#A655F8] shrink-0 border border-[#F3E8FF]">
                <FileSpreadsheet className="w-6 h-6 stroke-[1.8]" />
              </div>
              <div>
                <DialogTitle className="text-[20px] sm:text-[22px] font-bold text-slate-900 leading-tight">
                  {title}
                </DialogTitle>
                <p className="text-[13px] sm:text-[14px] text-slate-500 font-normal mt-0.5">
                  {description}
                </p>
              </div>
            </div>

            {/* Fixed columns card */}
            <div className="rounded-[16px] bg-[#FAF5FF] p-4 border border-[#F3E8FF] relative overflow-hidden">
              <div className="flex items-center gap-1.5 mb-3">
                <span className="text-[#A655F8] font-bold text-[13.5px]">Fixed columns</span>
                <span className="text-slate-500 text-[13px] font-normal">(always included)</span>
              </div>

              <div className="flex items-center gap-2 flex-wrap relative z-10">
                {fixedColumns.map((col) => {
                  const Icon = col.icon;
                  return (
                    <div
                      key={col.label}
                      className="bg-white rounded-[10px] px-3 py-1.5 border border-slate-100 flex items-center gap-1.5 shadow-sm text-[13px] font-semibold text-slate-800"
                    >
                      <Icon className="w-3.5 h-3.5 text-[#A655F8]" />
                      <span>{col.label}</span>
                    </div>
                  );
                })}
              </div>

              <div className="w-5 h-5 rounded-full bg-[#EDE9FE]/50 absolute top-4 right-4 pointer-events-none" />
            </div>

            {/* Optional columns */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="text-[15px] font-bold text-slate-900">Optional columns</h4>
                  <p className="text-[13px] text-slate-500 mt-0.5">
                    Select additional metrics and performance data.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleToggleSelectAll}
                  className="text-[#A655F8] text-[13px] font-semibold hover:underline flex items-center gap-1.5 cursor-pointer focus:outline-none"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  <span>{isAllSelected ? 'Deselect all' : 'Select all'}</span>
                </button>
              </div>

              <div className="grid grid-cols-2 gap-2.5 max-h-[220px] overflow-y-auto pr-1">
                {optionalColumns.map((col) => {
                  const isSelected = selectedColumns.includes(col.id);
                  const Icon = col.icon;

                  return (
                    <div
                      key={col.id}
                      onClick={() => toggleColumn(col.id)}
                      className={cn(
                        'rounded-[14px] border p-3 flex items-center gap-3 transition-all cursor-pointer select-none bg-white',
                        isSelected
                          ? 'border-[#C084FC] bg-[#FAF5FF]/40 shadow-xs'
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50/50'
                      )}
                    >
                      <Checkbox
                        id={`col-${col.id}`}
                        checked={isSelected}
                        onCheckedChange={() => toggleColumn(col.id)}
                        className="data-[state=checked]:bg-[#A655F8] data-[state=checked]:border-[#A655F8] rounded-[5px] w-5 h-5 border-slate-300 pointer-events-none"
                      />
                      <div className="w-8 h-8 rounded-[8px] bg-[#FAF5FF] flex items-center justify-center text-[#A655F8] shrink-0 border border-[#F3E8FF]">
                        <Icon className="w-4 h-4 stroke-[2]" />
                      </div>
                      <span className="text-[14px] font-semibold text-slate-800 truncate">
                        {col.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Security notice */}
            <div className="rounded-[14px] bg-[#FAF5FF] p-3.5 flex items-start gap-3 border border-[#F3E8FF]">
              <div className="w-7 h-7 rounded-full bg-[#EDE9FE] flex items-center justify-center text-[#A655F8] shrink-0 mt-0.5">
                <Shield className="w-4 h-4 stroke-[2]" />
              </div>
              <div className="text-left">
                <div className="font-bold text-[13px] text-slate-900 leading-snug">Your data is secure</div>
                <div className="text-[12px] text-slate-500 font-normal mt-0.5">
                  Exports are generated directly to your Google Drive and formatted with dedicated metrics.
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="pt-1">
              <Button
                type="button"
                onClick={handleExport}
                disabled={exportState !== 'idle'}
                className="w-full h-auto py-3.5 px-4 rounded-[14px] bg-[#A655F8] hover:bg-[#9333EA] text-white flex items-center justify-center group shadow-sm transition-all text-[15px] font-semibold gap-2"
              >
                {exportState === 'connecting' || exportState === 'exporting' ? (
                  <>
                    <LoadingSpinner size="small" className="text-white" />
                    <span>
                      {exportState === 'connecting'
                        ? 'Connecting to Google Sheets…'
                        : 'Generating Spreadsheet…'}
                    </span>
                  </>
                ) : (
                  <>
                    <FileSpreadsheet className="w-4 h-4 text-white" />
                    <span>
                      Export {entityName} {totalCount > 0 ? `(${totalCount} ${entityName})` : ''}
                    </span>
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
